import { ClipboardCheck, HeartPulse, PersonStanding, Utensils, Wind } from 'lucide-react';
import { DIET_TYPE, IPD_DIET_TEXTURES, IPD_OXYGEN_DEVICES, IPD_PHYSIOTHERAPY_TYPES, IPD_VITALS_PARAMETERS } from '@his/shared';
import { tr } from '@/lib/i18n-core';
import { FormSection } from '@/components/ui/form-screen';
import { Alert, Field, Input, Textarea } from '@/components/ui/primitives';
import { ChoiceButtons } from '@/components/ui/choice-buttons';
import { InstantPicker, ToggleChips } from './controls';
import type { FieldsProps } from './form-props';
import { everyLabel } from './order-labels';
import {
  FIO2_DEVICES,
  FLUID_LIMIT_CHOICES,
  OXYGEN_CHECK_INTERVALS,
  SPO2_PRESETS,
  VITALS_INTERVALS,
  type DietDraft,
  type NursingDraft,
  type OxygenDraft,
  type PhysiotherapyDraft,
  type VitalsMonitoringDraft,
} from './order-form';
import { FrequencyOrInterval, StopAtField } from './ScheduleFields';

const NO_LIMIT = 'none';
const CUSTOM = 'custom';
const SESSIONS = ['1', '2', '3', '4', '5', '6'] as const;

export function DietFields({ draft, update, error, common, setCommon }: FieldsProps<DietDraft>) {
  const limitChip = draft.fluidRestrictionMlPerDay.trim() === '' ? NO_LIMIT : (FLUID_LIMIT_CHOICES as readonly number[]).map(String).includes(draft.fluidRestrictionMlPerDay.trim()) ? draft.fluidRestrictionMlPerDay.trim() : '';
  return (
    <FormSection title={tr('ipd', 'io_form_section_diet')} icon={Utensils}>
      <Field label={tr('ipd', 'io_form_diet_type')} required error={error('dietType')} className="sm:col-span-2">
        <ChoiceButtons layout="chips" value={draft.dietType} options={DIET_TYPE.map((type) => ({ value: type, label: tr('ipd', `io_form_diet_${type}`) }))} onChange={(dietType) => update({ dietType })} />
      </Field>
      <Field label={tr('ipd', 'io_form_texture')} error={error('texture')} className="sm:col-span-2">
        <ChoiceButtons layout="chips" value={draft.texture} options={IPD_DIET_TEXTURES.map((texture) => ({ value: texture, label: tr('ipd', `io_form_texture_${texture}`) }))} onChange={(texture) => update({ texture })} />
      </Field>
      <Field label={tr('ipd', 'io_form_fluid_restriction')} error={error('fluidRestrictionMlPerDay')} className="sm:col-span-2">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
          <ChoiceButtons layout="chips"
            aria-label={tr('ipd', 'io_form_fluid_restriction')}
            value={limitChip}
            options={[{ value: NO_LIMIT, label: tr('ipd', 'io_form_no_limit') }, ...FLUID_LIMIT_CHOICES.map((choice) => ({ value: String(choice), label: String(choice) }))]}
            onChange={(value) => update({ fluidRestrictionMlPerDay: value === NO_LIMIT ? '' : value })}
          />
          <Input
            type="number"
            inputMode="numeric"
            min={100}
            max={5000}
            className="sm:w-32"
            aria-label={tr('ipd', 'io_form_fluid_restriction')}
            value={draft.fluidRestrictionMlPerDay}
            onChange={(event) => update({ fluidRestrictionMlPerDay: event.target.value })}
          />
        </div>
      </Field>
      <Field label={tr('ipd', 'io_form_npo_from')} error={error('npoFrom')}>
        <InstantPicker value={draft.npoFrom} onChange={(npoFrom) => update({ npoFrom })} />
      </Field>
      <Field label={tr('ipd', 'io_form_npo_until')} error={error('npoUntil')}>
        <InstantPicker value={draft.npoUntil} onChange={(npoUntil) => update({ npoUntil })} min={draft.npoFrom || undefined} />
      </Field>
      <Field label={tr('ipd', 'io_form_diet_notes')} error={error('notes')} className="sm:col-span-2">
        <Textarea rows={2} maxLength={600} value={draft.notes} onChange={(event) => update({ notes: event.target.value })} />
      </Field>
      <StopAtField common={common} setCommon={setCommon} error={error} />
      <Alert className="sm:col-span-2">{tr('ipd', 'io_form_diet_replaces')}</Alert>
    </FormSection>
  );
}

export function NursingFields({ draft, update, error, common, setCommon }: FieldsProps<NursingDraft>) {
  return (
    <FormSection title={tr('ipd', 'io_form_section_task')} icon={ClipboardCheck}>
      <Field label={tr('ipd', 'io_form_task')} required error={error('task')} className="sm:col-span-2">
        <Textarea rows={2} maxLength={240} placeholder={tr('ipd', 'io_form_task_placeholder')} value={draft.task} onChange={(event) => update({ task: event.target.value })} />
      </Field>
      <FrequencyOrInterval draft={draft} update={update} error={error} />
      <StopAtField common={common} setCommon={setCommon} error={error} />
    </FormSection>
  );
}

export function OxygenFields({ draft, update, error, common, setCommon }: FieldsProps<OxygenDraft>) {
  const leansFio2 = draft.device !== '' && FIO2_DEVICES.includes(draft.device);
  const preset = SPO2_PRESETS.find((range) => String(range.min) === draft.targetSpo2Min.trim() && String(range.max) === draft.targetSpo2Max.trim());
  const flow = (
    <Field key="flow" label={tr('ipd', 'io_form_flow')} hint={tr('ipd', 'io_form_flow_or_fio2')} hintBelow={!leansFio2} error={error('flowLpm')}>
      <Input type="number" inputMode="decimal" min={0.5} max={70} step="any" value={draft.flowLpm} onChange={(event) => update({ flowLpm: event.target.value })} />
    </Field>
  );
  const fio2 = (
    <Field key="fio2" label={tr('ipd', 'io_form_fio2')} hint={tr('ipd', 'io_form_flow_or_fio2')} hintBelow={leansFio2} error={error('fio2')}>
      <Input type="number" inputMode="numeric" min={21} max={100} value={draft.fio2} onChange={(event) => update({ fio2: event.target.value })} />
    </Field>
  );
  return (
    <FormSection title={tr('ipd', 'io_form_section_oxygen')} icon={Wind}>
      <Field label={tr('ipd', 'io_form_device')} required error={error('device')} className="sm:col-span-2">
        <ChoiceButtons layout="chips" value={draft.device} options={IPD_OXYGEN_DEVICES.map((device) => ({ value: device, label: tr('ipd', `io_form_device_${device}`) }))} onChange={(device) => update({ device })} />
      </Field>
      {leansFio2 ? [fio2, flow] : [flow, fio2]}
      <Field label={tr('ipd', 'io_form_target_spo2')} className="sm:col-span-2">
        <ChoiceButtons layout="chips"
          value={preset ? `${preset.min}-${preset.max}` : ''}
          options={SPO2_PRESETS.map((range) => ({ value: `${range.min}-${range.max}`, label: tr('ipd', 'io_form_spo2_range', { min: range.min, max: range.max }) }))}
          onChange={(value) => {
            const [min = '', max = ''] = value.split('-');
            update({ targetSpo2Min: min, targetSpo2Max: max });
          }}
        />
      </Field>
      <Field label={tr('ipd', 'io_form_spo2_min')} required error={error('targetSpo2Min')}>
        <Input type="number" inputMode="numeric" min={80} max={100} value={draft.targetSpo2Min} onChange={(event) => update({ targetSpo2Min: event.target.value })} />
      </Field>
      <Field label={tr('ipd', 'io_form_spo2_max')} required error={error('targetSpo2Max')}>
        <Input type="number" inputMode="numeric" min={80} max={100} value={draft.targetSpo2Max} onChange={(event) => update({ targetSpo2Max: event.target.value })} />
      </Field>
      <Field label={tr('ipd', 'io_form_check_every')} error={error('checkIntervalMinutes')} className="sm:col-span-2">
        <ChoiceButtons layout="chips"
          value={String(draft.checkIntervalMinutes)}
          options={OXYGEN_CHECK_INTERVALS.map((minutes) => ({ value: String(minutes), label: everyLabel(minutes) }))}
          onChange={(value) => update({ checkIntervalMinutes: Number(value) })}
        />
      </Field>
      <StopAtField common={common} setCommon={setCommon} error={error} />
    </FormSection>
  );
}

export function PhysiotherapyFields({ draft, update, error }: FieldsProps<PhysiotherapyDraft>) {
  return (
    <FormSection title={tr('ipd', 'io_form_section_physio')} icon={PersonStanding}>
      <Field label={tr('ipd', 'io_form_physio_type')} required error={error('type')} className="sm:col-span-2">
        <ChoiceButtons layout="chips" value={draft.type} options={IPD_PHYSIOTHERAPY_TYPES.map((type) => ({ value: type, label: tr('ipd', `io_form_physio_${type}`) }))} onChange={(type) => update({ type })} />
      </Field>
      <Field label={tr('ipd', 'io_form_sessions_per_day')} error={error('sessionsPerDay')}>
        <ChoiceButtons layout="chips" value={String(draft.sessionsPerDay)} options={SESSIONS.map((count) => ({ value: count, label: count }))} onChange={(value) => update({ sessionsPerDay: Number(value) })} />
      </Field>
      <Field label={tr('ipd', 'io_form_duration_days')} hint={tr('ipd', 'io_form_physio_days_hint')} hintBelow error={error('durationDays')}>
        <Input type="number" inputMode="numeric" min={1} max={90} value={draft.durationDays} onChange={(event) => update({ durationDays: event.target.value })} />
      </Field>
      <Field label={tr('ipd', 'io_form_notes')} error={error('notes')} className="sm:col-span-2">
        <Textarea rows={2} maxLength={600} value={draft.notes} onChange={(event) => update({ notes: event.target.value })} />
      </Field>
    </FormSection>
  );
}

export function VitalsMonitoringFields({ draft, update, error, common, setCommon }: FieldsProps<VitalsMonitoringDraft>) {
  const chosen = draft.custom ? CUSTOM : draft.intervalMinutes;
  return (
    <FormSection title={tr('ipd', 'io_form_section_vitals')} icon={HeartPulse}>
      <Field label={tr('ipd', 'io_form_vitals_interval')} required error={draft.custom ? undefined : error('intervalMinutes')} className="sm:col-span-2">
        <ChoiceButtons layout="chips"
          value={chosen}
          options={[...VITALS_INTERVALS.map((minutes) => ({ value: String(minutes), label: everyLabel(minutes) })), { value: CUSTOM, label: tr('ipd', 'io_form_custom') }]}
          onChange={(value) => (value === CUSTOM ? update({ custom: true }) : update({ custom: false, intervalMinutes: value }))}
        />
      </Field>
      {draft.custom && (
        <Field label={tr('ipd', 'io_form_custom_minutes')} required error={error('intervalMinutes')}>
          <Input type="number" inputMode="numeric" min={15} max={1440} step={5} value={draft.intervalMinutes} onChange={(event) => update({ intervalMinutes: event.target.value })} />
        </Field>
      )}
      <Field label={tr('ipd', 'io_form_parameters')} required error={error('parameters')} className="sm:col-span-2">
        <ToggleChips values={draft.parameters} options={IPD_VITALS_PARAMETERS.map((parameter) => ({ value: parameter, label: tr('ipd', `io_form_param_${parameter}`) }))} onChange={(parameters) => update({ parameters })} />
      </Field>
      <StopAtField common={common} setCommon={setCommon} error={error} />
      <Alert className="sm:col-span-2">{tr('ipd', 'io_form_vitals_replaces')}</Alert>
    </FormSection>
  );
}
